import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import './CruiseInquiryModal.css';

const CruiseInquiryModal = ({ isOpen, onClose, showToast }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        destination: '',
        travelDate: '',
        passengers: 1,
        duration: '',
        cabinType: 'interior',
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        // Format notes field to include cruise-specific details
        const notesFormatted = `Destination: ${formData.destination} | Duration: ${formData.duration} | Cabin: ${formData.cabinType} | Notes: ${formData.notes || 'N/A'}`;

        const inquiryData = {
            type: 'cruise',
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            country: formData.destination,
            travel_date: formData.travelDate || null,
            passengers: parseInt(formData.passengers) || 1,
            notes: notesFormatted,
            status: 'new'
        };

        const { error } = await supabase.from('inquiries').insert([inquiryData]);

        setSubmitting(false);

        if (error) {
            console.error('Error submitting cruise inquiry:', error);
            console.error('Error details:', JSON.stringify(error, null, 2));
            console.error('Inquiry data:', inquiryData);
            showToast?.(`Failed to submit inquiry: ${error.message || 'Please try again.'}`, 'error');
        } else {
            showToast?.('Cruise inquiry submitted successfully! We will contact you soon.', 'success');
            setFormData({
                name: '',
                phone: '',
                email: '',
                destination: '',
                travelDate: '',
                passengers: 1,
                duration: '',
                cabinType: 'interior',
                notes: ''
            });
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="cruise-modal-overlay" onClick={onClose}>
            <div className="cruise-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="cruise-modal-close" onClick={onClose}>
                    <i className="fa-solid fa-times"></i>
                </button>

                <div className="cruise-modal-header">
                    <div className="cruise-modal-icon">
                        <i className="fa-solid fa-ship"></i>
                    </div>
                    <h2>Cruise Inquiry</h2>
                    <p>Share your cruise preferences and we'll help you plan the perfect voyage</p>
                </div>

                <form onSubmit={handleSubmit} className="cruise-form">
                    <div className="cruise-form-row">
                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-user"></i> Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter your name"
                                required
                            />
                        </div>

                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-phone"></i> Phone Number *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+91 98765 43210"
                                required
                            />
                        </div>
                    </div>

                    <div className="cruise-form-group">
                        <label>
                            <i className="fa-solid fa-envelope"></i> Email Address
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div className="cruise-form-row">
                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-anchor"></i> Cruise Destination *
                            </label>
                            <select
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select destination</option>
                                <option value="Caribbean">Caribbean</option>
                                <option value="Mediterranean">Mediterranean</option>
                                <option value="Alaska">Alaska</option>
                                <option value="Dubai & Arabia">Dubai & Arabia</option>
                                <option value="Asia Pacific">Asia Pacific</option>
                                <option value="Northern Europe">Northern Europe</option>
                                <option value="South America">South America</option>
                                <option value="World Cruise">World Cruise</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-calendar"></i> Preferred Travel Date
                            </label>
                            <input
                                type="date"
                                name="travelDate"
                                value={formData.travelDate}
                                onChange={handleChange}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>

                    <div className="cruise-form-row">
                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-users"></i> Number of Passengers *
                            </label>
                            <input
                                type="number"
                                name="passengers"
                                value={formData.passengers}
                                onChange={handleChange}
                                min="1"
                                max="20"
                                required
                            />
                        </div>

                        <div className="cruise-form-group">
                            <label>
                                <i className="fa-solid fa-clock"></i> Cruise Duration
                            </label>
                            <select
                                name="duration"
                                value={formData.duration}
                                onChange={handleChange}
                            >
                                <option value="">Select duration</option>
                                <option value="3-5 days">3-5 days</option>
                                <option value="6-8 days">6-8 days</option>
                                <option value="9-12 days">9-12 days</option>
                                <option value="13+ days">13+ days</option>
                            </select>
                        </div>
                    </div>

                    <div className="cruise-form-group">
                        <label>
                            <i className="fa-solid fa-bed"></i> Cabin Type Preference
                        </label>
                        <div className="cruise-cabin-options">
                            <label className="cruise-cabin-option">
                                <input
                                    type="radio"
                                    name="cabinType"
                                    value="interior"
                                    checked={formData.cabinType === 'interior'}
                                    onChange={handleChange}
                                />
                                <span>Interior</span>
                            </label>
                            <label className="cruise-cabin-option">
                                <input
                                    type="radio"
                                    name="cabinType"
                                    value="oceanview"
                                    checked={formData.cabinType === 'oceanview'}
                                    onChange={handleChange}
                                />
                                <span>Ocean View</span>
                            </label>
                            <label className="cruise-cabin-option">
                                <input
                                    type="radio"
                                    name="cabinType"
                                    value="balcony"
                                    checked={formData.cabinType === 'balcony'}
                                    onChange={handleChange}
                                />
                                <span>Balcony</span>
                            </label>
                            <label className="cruise-cabin-option">
                                <input
                                    type="radio"
                                    name="cabinType"
                                    value="suite"
                                    checked={formData.cabinType === 'suite'}
                                    onChange={handleChange}
                                />
                                <span>Suite</span>
                            </label>
                        </div>
                    </div>

                    <div className="cruise-form-group">
                        <label>
                            <i className="fa-solid fa-comment"></i> Additional Notes
                        </label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Any special requests or preferences..."
                            rows="3"
                        ></textarea>
                    </div>

                    <button type="submit" className="cruise-submit-btn" disabled={submitting}>
                        {submitting ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin"></i>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-paper-plane"></i>
                                Submit Inquiry
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CruiseInquiryModal;
